// src/services/audioService.js
import { openDB } from 'idb'; // NOTE: You should install this helper: npm install idb

const DB_NAME = 'OkulAudioDB';
const STORE_NAME = 'recordings';
const STORE_AUDIO = 'recordings';     // Your existing store
const STORE_STATUS = 'upload_status'; // NEW store for tracking

// 1. In-Memory Map to track ACTIVE recorders (not saved yet)
// Key: questionId, Value: { recorder, stream, chunks: [] }
const activeRecorders = new Map();

/**
 * Initialize the IndexedDB
 */
export const initDB = async () => {
  return openDB(DB_NAME, 2, { // <--- INCREASE VERSION TO 2
    upgrade(db) {
      // 1. Create Audio Store (if not exists)
      if (!db.objectStoreNames.contains(STORE_AUDIO)) {
        db.createObjectStore(STORE_AUDIO);
      }
      
      // 2. Create Status Store (NEW)
      if (!db.objectStoreNames.contains(STORE_STATUS)) {
        const statusStore = db.createObjectStore(STORE_STATUS, { keyPath: 'id' });
      }
    },
  });
};

/**
 * START Recording
 * - If a recording exists for this ID in DB, it will be overwritten on save.
 * - If a recording is currently active in RAM, it restarts it.
 */
export const startRecording = async (id) => {
  if (!id) throw new Error("ID required");

  // Cleanup existing active session if user clicked record twice quickly
  if (activeRecorders.has(id)) {
    await stopRecording(id, false); // Stop without saving
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
    const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
    const options = mimeType ? { mimeType } : {};
    const recorder = new MediaRecorder(stream, options);
    
    const chunks = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.start();

    // Save to our active memory map
    activeRecorders.set(id, { recorder, stream, chunks });
    
    return { success: true };
  } catch (err) {
    console.error("Mic Error:", err);
    return { error: "Microphone access denied" };
  }
};

/**
 * PAUSE Recording
 */
export const pauseRecording = (id) => {
  const session = activeRecorders.get(id);
  if (session && session.recorder.state === "recording") {
    session.recorder.pause();
    return true;
  }
  return false;
};

/**
 * RESUME Recording
 */
export const resumeRecording = (id) => {
  const session = activeRecorders.get(id);
  if (session && session.recorder.state === "paused") {
    session.recorder.resume();
    return true;
  }
  return false;
};

/**
 * RESTART Recording
 * - Wipes current chunks for this ID and starts fresh.
 */
export const restartRecording = async (id) => {
  const session = activeRecorders.get(id);
  if (session) {
    // Stop the actual recorder logic but keep the stream open if possible? 
    // Easier to just fully stop and re-init to avoid bugs.
    await stopRecording(id, false); // False = Do not save to DB
  }
  return await startRecording(id);
};

/**
 * STOP Recording & SAVE to IndexedDB
 * @param {string} id - The Question ID
 * @param {boolean} saveToDb - If false, just cleanup (discard).
 */
export const stopRecording = (id, saveToDb = true) => {
  return new Promise((resolve) => {
    const session = activeRecorders.get(id);
    if (!session) return resolve({ error: "No active recording found" });

    const { recorder, stream, chunks } = session;

    recorder.onstop = async () => {
      // 1. Cleanup Stream
      stream.getTracks().forEach((track) => track.stop());
      activeRecorders.delete(id); // Remove from active memory

      if (saveToDb) {
        // 2. Create Blob
        const type = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
        const blob = new Blob(chunks, { type });

        // 3. Save to IndexedDB (Persist across reloads)
        const db = await initDB();
        await db.put(STORE_NAME, blob, id); // 'put' automatically overwrites if ID exists
        
        // 4. Return URL for immediate playback
        const url = URL.createObjectURL(blob);
        resolve({ url, blob });
      } else {
        resolve({ status: "discarded" });
      }
    };

    recorder.stop();
  });
};

/**
 * GET Audio from IDB
 * Used by the search page or player.
 */
export const getAudioFromDB = async (id) => {
  const db = await initDB();
  const blob = await db.get(STORE_NAME, id);
  
  if (!blob) return null;
  
  // Convert saved Blob to playable URL
  return URL.createObjectURL(blob);
};

export const getAudioBlobFromDB = async (id) => {
  const db = await initDB();
  const blob = await db.get(STORE_NAME, id);
  return blob || null; // <-- Blob object
};


/**
 * Save/Update Upload Status
 * @param {string} id - Question UUID
 * @param {'success' | 'retry' | 'pending'} status 
 */
export const updateUploadStatus = async (id, index, testId, uri , status) => {
  const db = await initDB();
  await db.put(STORE_STATUS, {
    id: id,
    status: status,
    uri: uri,
    createdAt: new Date().toISOString(),
    testId:testId,
    index: index
  });
};

export const getUploadStatusByTestId = async (testId) => {
  const db = await initDB();
  const all = await db.getAll(STORE_STATUS); // simplest (ok for small scale)
  return all.filter(r => r.testId === testId);
};


/**
 * Get Status (Useful for showing green ticks or red retry buttons in UI)
 */
export const getUploadStatus = async (id) => {
  const db = await initDB();
  return await db.get(STORE_STATUS, id);
};

// src/services/audioService.js

/**
 * Permanently delete the Audio Blob from IDB
 * Call this to free up space.
 */
export const deleteAudioFromDB = async (id) => {
  const db = await initDB();
  await db.delete('recordings', id); // 'recordings' is your STORE_NAME constant
};

/**
 * Permanently delete the Status object from IDB
 */
export const deleteStatusFromDB = async (id) => {
  const db = await initDB();
  await db.delete('upload_status', id); // 'upload_status' is your status store constant
};

/**
 * Wipe EVERYTHING for a specific question (Audio + Status)
 * Use this when the workflow is 100% complete.
 */
export const clearLocalData = async (id) => {
  await Promise.all([
    deleteAudioFromDB(id),
    deleteStatusFromDB(id)
  ]);
  console.log(`Cleaned up local storage for ID: ${id}`);
};